import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

export default function AdminPuppiesPage({
                                             primaryColor = "#8D6E63",
                                             secondaryColor = "#A1887F",
                                         }) {
    const navigate = useNavigate();
    const { id } = useParams();

    const [litter, setLitter] = useState(null);
    const [puppies, setPuppies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const loadData = useCallback(async () => {
        if (!id) return;

        setIsLoading(true);
        setErrorMessage("");

        const { data: litterData, error: litterError } = await supabase
            .from("litters")
            .select("id, name, birth_date")
            .eq("id", id)
            .single();

        if (litterError) {
            console.error("Fehler beim Laden des Wurfs:", litterError);
            setErrorMessage("Der Wurf konnte nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        const { data: puppiesData, error: puppiesError } = await supabase
            .from("puppies")
            .select("*")
            .eq("litter_id", id)
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: true });

        if (puppiesError) {
            console.error("Fehler beim Laden der Welpen:", puppiesError);
            setErrorMessage("Die Welpen konnten nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setLitter(litterData);
        setPuppies(puppiesData || []);
        setIsLoading(false);
    }, [id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    async function handleDelete(puppyId) {
        const confirmed = window.confirm(
            "Möchtest du diesen Welpen wirklich löschen?"
        );

        if (!confirmed) return;

        const { error } = await supabase
            .from("puppies")
            .delete()
            .eq("id", puppyId);

        if (error) {
            console.error("Fehler beim Löschen:", error);
            alert("Der Welpe konnte nicht gelöscht werden.");
            return;
        }

        setPuppies((current) => current.filter((puppy) => puppy.id !== puppyId));
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Welpen werden geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            Welpen verwalten
                        </h1>
                        <p style={adminStyles.subtitle}>
                            {litter?.name
                                ? `Welpen für ${litter.name} hinzufügen, bearbeiten oder löschen.`
                                : "Welpen hinzufügen, bearbeiten oder löschen."}
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => navigate("/admin/litters")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: secondaryColor,
                            }}
                        >
                            Zurück zu Würfen
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate(`/admin/litters/${id}/puppies/new`)}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                            }}
                        >
                            Neuer Welpe
                        </button>
                    </div>
                </header>

                {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                {puppies.length === 0 ? (
                    <motion.div
                        style={adminStyles.dashboardCard}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h2 style={{ marginTop: 0, color: primaryColor }}>
                            Noch keine Welpen vorhanden
                        </h2>

                        <p style={{ color: "#666", lineHeight: 1.7 }}>
                            Lege den ersten Welpen für diesen Wurf an.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate(`/admin/litters/${id}/puppies/new`)}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                marginTop: "1rem",
                            }}
                        >
                            Welpe erstellen
                        </button>
                    </motion.div>
                ) : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        {puppies.map((puppy, index) => (
                            <motion.article
                                key={puppy.id}
                                style={{
                                    ...adminStyles.dashboardCard,
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                                    gap: "1rem",
                                    alignItems: "center",
                                }}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.35,
                                    delay: index * 0.04,
                                }}
                            >
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "90px minmax(0, 1fr)",
                                        gap: "1rem",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "90px",
                                            height: "90px",
                                            borderRadius: "18px",
                                            overflow: "hidden",
                                            backgroundColor: "#f1ebe6",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#777",
                                            fontSize: "0.8rem",
                                            textAlign: "center",
                                        }}
                                    >
                                        {puppy.image ? (
                                            <img
                                                src={puppy.image}
                                                alt={puppy.name}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                }}
                                            />
                                        ) : (
                                            "Kein Bild"
                                        )}
                                    </div>

                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "0.5rem",
                                                flexWrap: "wrap",
                                                marginBottom: "0.65rem",
                                            }}
                                        >
                                            {puppy.gender && (
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "0.3rem 0.75rem",
                                                        borderRadius: "999px",
                                                        backgroundColor: `${primaryColor}18`,
                                                        color: primaryColor,
                                                        fontSize: "0.85rem",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {puppy.gender}
                                                </span>
                                            )}

                                            {puppy.status && (
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "0.3rem 0.75rem",
                                                        borderRadius: "999px",
                                                        backgroundColor: "#f1f1f1",
                                                        color: "#666",
                                                        fontSize: "0.85rem",
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {puppy.status}
                                                </span>
                                            )}
                                        </div>

                                        <h2
                                            style={{
                                                margin: "0 0 0.4rem",
                                                color: primaryColor,
                                                fontSize: "1.35rem",
                                                lineHeight: 1.35,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {puppy.name}
                                        </h2>

                                        <p
                                            style={{
                                                margin: "0 0 0.35rem",
                                                color: "#666",
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            Farbe: {puppy.color || "Nicht angegeben"}
                                        </p>

                                        <p
                                            style={{
                                                margin: 0,
                                                color: "#888",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            Sortierung: {puppy.sort_order ?? 0}
                                        </p>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "0.5rem",
                                        flexWrap: "wrap",
                                        justifyContent: "flex-end",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                `/admin/litters/${id}/puppies/${puppy.id}/edit`
                                            )
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: secondaryColor,
                                        }}
                                    >
                                        Bearbeiten
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(puppy.id)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            backgroundColor: "#b94a48",
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}